import { getPublicClient } from '@wagmi/core';

import { AxiosResponse } from 'axios';
import { type Address } from 'viem';

import { wagmiConfig } from '@/connectors/wagmi/config';
import { SYN_API_URLS } from '@/constants/api';
import { DAPP_CHAIN_CONFIGS } from '@/constants/chain';
import {
  BaseResponse,
  EnableAgentReqDto,
  EnableAgentRespDto,
  JwtTokenRespDto,
  SendGelatoTransactionRespDto,
  TxLoginRequest,
  TxLoginResponse,
  TxSubmitRequest,
  TxSubmitResponse,
  UserLoginReqDto,
} from '@/types/agent';
import { axiosPost } from '@/utils/axios';
import { getGasSettings } from '@/utils/chain';
import { useAgentStore } from './store';

/**
 * login
 * POST /login
 */
export const gelatoLogin = async (data: UserLoginReqDto): Promise<AxiosResponse<BaseResponse<JwtTokenRespDto>>> => {
  return axiosPost({
    url: SYN_API_URLS.ONE_CT.GELATO_LOGIN,
    data,
  });
};

export const enableOrDisableGelatoAgent = async (
  data: EnableAgentReqDto,
  jwtToken?: string,
): Promise<AxiosResponse<BaseResponse<EnableAgentRespDto>>> => {
  return axiosPost({
    url: SYN_API_URLS.ONE_CT.GELATO_SUBAccount,
    data,
    jwtToken: `Bearer ${jwtToken}`,
  });
};

export const sendGelatoTransactionFromServer = async (
  data: {
    chainId: number;
    batchData: string;
    targetContract: string;
  },
  jwtToken?: string,
): Promise<AxiosResponse<BaseResponse<SendGelatoTransactionRespDto>>> => {
  return axiosPost({
    url: SYN_API_URLS.ONE_CT.GELATO_OPERATION,
    data,
    jwtToken: `Bearer ${jwtToken}`,
  });
};

export const sendTransactionFromServer = async (
  data: TxSubmitRequest,
  jwtToken?: string,
): Promise<AxiosResponse<BaseResponse<TxSubmitResponse>>> => {
  try {
    /**
     * Submit a signed transaction batch to the Frontend TX service.
     * This uses the new `/v4/public/frontend-tx/submit` endpoint.
     * Authorization header should include the Bearer token obtained from txLogin.
     */
    // Try to estimate gasLimit on-the-fly if not provided
    let gasLimit = data.gasLimit;

    if (!gasLimit) {
      try {
        gasLimit = await estimateGasLimitForTx(data);
      } catch (e) {
        console.error(e);
        const fallback = getGasSettings().gasLimit as number;
        gasLimit = String(fallback ?? 2500000);
      }
    }
    const result = await axiosPost({
      url: SYN_API_URLS.ONE_CT.TX_SUBMIT,
      data: {
        ...data,
        gasLimit: gasLimit,
        gasPrice: data.gasPrice,
        value: data.value || '0',
      },
      jwtToken: `Bearer ${jwtToken}`,
    });

    return result;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.log('🚀 ~ sendTransactionFromServer ~ error:', error);
    if (error?.response?.data?.msg === 'Invalid token') {
      const { setJWTToken } = useAgentStore.getState();
      setJWTToken(data.userAddr, undefined);
    }
    console.error(error);
    throw error;
  }
};

/**
 * Estimate gasLimit for a raw transaction using viem PublicClient.
 * - Uses chain RPC from `DAPP_CHAIN_CONFIGS`.
 * - Builds a call with `to`, `calldata`, and optional `value`.
 * - Uses a zero address as the `from` for estimation when actual sender is unknown.
 *
 * Note: For maximum accuracy (especially for proxy-based flows), consider
 * passing the actual sender address if available in the future.
 */
async function estimateGasLimitForTx(tx: TxSubmitRequest): Promise<string> {
  const chainConfig = DAPP_CHAIN_CONFIGS[tx.chainID];
  const publicClient = getPublicClient(wagmiConfig);
  if (!chainConfig || !publicClient) {
    const fallback = getGasSettings().gasLimit as number;
    return String(fallback ?? 2500000);
  }

  const valueBigInt = tx.value ? BigInt(tx.value) : undefined;

  const agentSettings = useAgentStore.getState().userAgentSettings[tx.userAddr];
  const agentAddr = agentSettings?.agentAddress;

  let estimated = await publicClient.estimateGas({
    // Use zero address as a neutral `from` when proxyAddr is not available
    account: (agentAddr || tx.userAddr) as Address,
    to: tx.to as Address,
    data: tx.calldata as `0x${string}`,
    value: valueBigInt,
  });
  if (estimated) {
    estimated = (estimated * BigInt(12)) / BigInt(10);
  }

  return estimated.toString();
}

/**
 * login
 * POST /login
 */
export const txLogin = async (data: TxLoginRequest): Promise<AxiosResponse<BaseResponse<TxLoginResponse>>> => {
  return axiosPost({
    url: SYN_API_URLS.ONE_CT.TX_LOGIN,
    data,
  });
};
